import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SignaturePad } from "@/components/ui/SignaturePad";
import { toast } from "sonner";
import { Check, X, Building2, CheckCircle2, XCircle } from "lucide-react";
import {
  getPublicChangeOrderApproval,
  type PublicChangeOrderPayload,
} from "@/utils/change-orders.functions";

export const Route = createFileRoute("/change-orders/approve/$token")({
  component: PublicChangeOrderApprovalPage,
});

function PublicChangeOrderApprovalPage() {
  const { token } = Route.useParams();
  const { t, i18n } = useTranslation();
  const [payload, setPayload] = useState<PublicChangeOrderPayload>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState("");
  const [signName, setSignName] = useState("");
  const [signature, setSignature] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pdfVersion, setPdfVersion] = useState(0);
  const [pdfObjectUrl, setPdfObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const browserLang = (i18n.language || "").slice(0, 2).toLowerCase();
        const data = await getPublicChangeOrderApproval({
          data: { token, target_language: browserLang },
        });
        if (cancelled) return;
        if (!data?.token || !data.change_order) {
          setError(t("pages.changeOrders.approval.invalidLink"));
        } else {
          if (data.language && data.language !== i18n.language) {
            void i18n.changeLanguage(data.language);
          }
          setPayload(data);
        }
      } catch (e: any) {
        setError(e?.message ?? t("pages.changeOrders.approval.loadFailed"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, t, i18n]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    (async () => {
      setPdfObjectUrl(null);
      try {
        const response = await fetch(
          `/api/public/change-order-pdf/${token}?language=${encodeURIComponent(i18n.language)}&v=${pdfVersion}`,
          { credentials: "same-origin" },
        );
        if (!response.ok) throw new Error("PDF request failed");
        const blob = await response.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
        setPdfObjectUrl(objectUrl);
      } catch {
        if (!cancelled) setPdfObjectUrl(null);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [token, i18n.language, pdfVersion]);

  const respond = async (decision: "approved" | "rejected") => {
    if (!payload?.token) return;
    if (decision === "approved") {
      if (!signName.trim()) {
        toast.error(t("pages.signature.signerNameRequired"));
        return;
      }
      if (!signature) {
        toast.error(t("pages.signature.signatureRequired"));
        return;
      }
    }
    setSubmitting(true);
    const { error } = await supabase.rpc("respond_to_change_order_token", {
      _token: token,
      _decision: decision,
      _response_notes: notes.trim() || "",
      _client_signature_name: decision === "approved" ? signName.trim() : "",
      _client_signature_data: decision === "approved" ? (signature ?? "") : "",
    });
    setSubmitting(false);
    if (error) {
      toast.error(t("pages.changeOrders.approval.submitFailed"), { description: error.message });
      return;
    }
    setPayload({
      ...payload,
      token: {
        ...payload.token,
        status: decision,
        response_notes: notes.trim() || null,
        responded_at: new Date().toISOString(),
        client_signature_name: decision === "approved" ? signName.trim() : null,
        client_signature_data: decision === "approved" ? (signature ?? null) : null,
        client_signed_at: decision === "approved" ? new Date().toISOString() : null,
      },
    });
    setPdfVersion((v) => v + 1);
    toast.success(
      decision === "approved"
        ? t("pages.changeOrders.approval.approvedToast")
        : t("pages.changeOrders.approval.rejectedToast"),
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error || !payload?.change_order || !payload.token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="max-w-md rounded-lg border border-border bg-card p-8 text-center">
          <XCircle className="mx-auto h-10 w-10 text-destructive" />
          <h1 className="mt-3 text-xl font-semibold">
            {t("pages.changeOrders.approval.linkUnavailable")}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {error ?? t("pages.changeOrders.approval.loadFailed")}
          </p>
        </div>
      </div>
    );
  }

  const co = payload.change_order;
  const tokenRow = payload.token;
  const alreadyResponded = tokenRow.status !== "sent";
  const wasApproved = tokenRow.status === "approved";
  const wasRejected = tokenRow.status === "rejected";
  const openPdfInNewTab = () => {
    if (!pdfObjectUrl) {
      toast.error(t("pages.changeOrders.approval.openPdfFailed", "Não foi possível abrir o PDF."));
      return;
    }
    const target = window.open(pdfObjectUrl, "_blank");
    if (target) target.opener = null;
  };

  const downloadPdf = () => {
    if (!pdfObjectUrl) {
      toast.error(t("pages.changeOrders.approval.downloadPdfFailed", "Não foi possível baixar o PDF."));
      return;
    }
    const link = document.createElement("a");
    link.href = pdfObjectUrl;
    link.download = `change-order-${co.number}.pdf`;
    link.click();
  };

  return (
    <div className="min-h-screen bg-muted/30 py-8 px-4">
      <style>{`@media print {
        body { background: white !important; }
        .no-print { display: none !important; }
        .print-area { box-shadow: none !important; border: none !important; }
      }`}</style>
      <div className="mx-auto max-w-3xl space-y-6 print-area">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {co.companies?.logo_url ? (
              <img
                src={co.companies.logo_url}
                alt={co.companies.name}
                className="h-10 w-10 rounded object-cover"
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded bg-primary/10">
                <Building2 className="h-5 w-5 text-primary" />
              </div>
            )}
            <div>
              <p className="text-sm font-semibold">
                {co.companies?.name ?? t("pages.changeOrders.approval.companyFallback")}
              </p>
              {co.companies?.email && (
                <p className="text-xs text-muted-foreground">{co.companies.email}</p>
              )}
            </div>
          </div>
          <Badge variant="outline" className="font-mono text-xs">
            {co.number}
          </Badge>
        </div>

        {alreadyResponded && (
          <div
            className={`flex items-start gap-3 rounded-lg border p-4 ${
              wasApproved
                ? "border-emerald-200 bg-emerald-50"
                : wasRejected
                  ? "border-rose-200 bg-rose-50"
                  : "border-amber-200 bg-amber-50"
            }`}
          >
            {wasApproved ? (
              <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" />
            ) : (
              <XCircle className="mt-0.5 h-5 w-5 text-rose-600" />
            )}
            <div className="flex-1">
              <p className="font-semibold">
                {wasApproved
                  ? t("pages.changeOrders.approval.youApproved")
                  : wasRejected
                    ? t("pages.changeOrders.approval.youDeclined")
                    : t("pages.changeOrders.approval.linkRevoked")}
              </p>
              {tokenRow.responded_at && (
                <p className="text-xs text-muted-foreground">
                  {new Date(tokenRow.responded_at).toLocaleString(i18n.language)}
                </p>
              )}
              {tokenRow.response_notes && <p className="mt-2 text-sm">{tokenRow.response_notes}</p>}
            </div>
            {wasApproved && (
              <button
                type="button"
                onClick={downloadPdf}
                className="no-print inline-flex items-center text-sm font-medium text-primary hover:underline"
              >
                {t("pages.changeOrders.approval.downloadPdf", "Baixar PDF")}
              </button>
            )}
          </div>
        )}

        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <div className="flex items-center justify-between border-b border-border bg-muted/40 px-4 py-2">
            <p className="text-sm font-semibold">
              {t("pages.changeOrders.approval.documentPreview", "Documento para assinar")}
            </p>
            <button
              type="button"
              onClick={openPdfInNewTab}
              className="text-xs font-medium text-primary hover:underline"
            >
              {t("pages.changeOrders.approval.openPdf", "Abrir em nova aba")}
            </button>
          </div>
          {pdfObjectUrl ? (
            <iframe
              title="change-order-pdf"
              src={`${pdfObjectUrl}#toolbar=0&navpanes=0`}
              className="h-[80vh] w-full bg-white"
            />
          ) : (
            <div className="flex h-[80vh] items-center justify-center bg-white text-sm text-muted-foreground">
              {t("common.loading")}
            </div>
          )}
        </div>

        {!alreadyResponded && (
          <div className="rounded-lg border border-border bg-card p-6 space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="co-signer-name">{t("pages.signature.signerNameLabel")}</Label>
              <Input
                id="co-signer-name"
                value={signName}
                onChange={(e) => setSignName(e.target.value)}
                placeholder={t("pages.signature.signerNamePlaceholder")}
              />
            </div>
            <SignaturePad
              value={signature}
              onChange={setSignature}
              label={t("pages.signature.signatureLabel")}
              hint={t("pages.signature.signatureHint")}
              clearLabel={t("pages.signature.signatureClear")}
            />
            <label className="text-sm font-medium">
              {t("pages.changeOrders.approval.comments")}
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("pages.changeOrders.approval.commentsPlaceholder")}
              rows={3}
            />
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button onClick={() => respond("approved")} disabled={submitting} className="flex-1">
                <Check className="mr-2 h-4 w-4" />
                {t("pages.changeOrders.approval.approveBtn")}
              </Button>
              <Button
                onClick={() => respond("rejected")}
                disabled={submitting}
                variant="destructive"
                className="flex-1"
              >
                <X className="mr-2 h-4 w-4" />
                {t("pages.changeOrders.approval.rejectBtn")}
              </Button>
            </div>
          </div>
        )}

        {wasApproved && tokenRow.client_signature_data && (
          <div className="rounded-lg border border-border bg-card p-6">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {t("pages.signature.signatureLabel", "Assinatura")}
            </h2>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-6">
              <img
                src={tokenRow.client_signature_data}
                alt="signature"
                className="h-24 w-auto max-w-xs border-b border-border bg-white object-contain"
              />
              <div className="text-sm">
                {tokenRow.client_signature_name && (
                  <p className="font-semibold">{tokenRow.client_signature_name}</p>
                )}
                {tokenRow.client_signed_at && (
                  <p className="text-xs text-muted-foreground">
                    {new Date(tokenRow.client_signed_at).toLocaleString(i18n.language)}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
